import React, { useState, useEffect } from 'react';
import { authFetch } from '../services/authFetch';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TableSortLabel,
} from '@mui/material';
import { FileDownload } from '@mui/icons-material';
import { POSTMORTEM } from '../config/api';

interface WeekData {
  weekLabel: string;
  closed: number;
  missed: number;
  openWithinDue: number;
}

interface ActionStatusData {
  portfolio: string;
  weeks: WeekData[];
  openDueWithin15Days: number;
  openDueAfter15Days: number;
  openPastDueDate: number;
}

interface PostmortemActionStatusProps {
  selectedPortfolio?: string;
  selectedPlatform?: string;
  postmortemDetailsData?: any[];
}

const PostmortemActionStatus: React.FC<PostmortemActionStatusProps> = ({
  selectedPortfolio,
  selectedPlatform,
  postmortemDetailsData = [],
}) => {
  const [statusData, setStatusData] = useState<ActionStatusData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [weeks, setWeeks] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState<any[]>([]);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogType, setDialogType] = useState<string>('');
  const [allActionsData, setAllActionsData] = useState<any[]>([]);
  const [closedDatesMap, setClosedDatesMap] = useState<Map<string, Date>>(new Map());
  const [selectedWeeks, setSelectedWeeks] = useState<number>(2);

  useEffect(() => {
    processActionData();
  }, [selectedPortfolio, selectedPlatform, postmortemDetailsData, selectedWeeks]);

  const fetchClosedDates = async (): Promise<Map<string, Date>> => {
    try {
      const response = await authFetch(POSTMORTEM.ENHANCEMENTS);
      if (!response.ok) {
        console.error('Failed to fetch postmortem enhancements');
        return new Map();
      }

      const data = await response.json();
      const enhancements = data.enhancements || [];
      
      const closedMap = new Map<string, Date>();
      
      enhancements.forEach((enhancement: any) => {
        if (enhancement.closedDates) {
          Object.entries(enhancement.closedDates).forEach(([actionKey, closedDateStr]: [string, any]) => {
            // actionKey format: "PTASK_PTASK0035371" or "JIRA_OME-35834"
            // Extract the actual action ID
            const actionId = actionKey.replace('PTASK_', '').replace('JIRA_', '');
            
            try {
              let closedDate: Date;
              
              // Handle different date formats
              if (typeof closedDateStr === 'string') {
                if (closedDateStr.includes('T')) {
                  // ISO format: "2025-01-06T07:05:36.473+0000"
                  closedDate = new Date(closedDateStr);
                } else {
                  // Format: "2024-09-23 07:53:34"
                  closedDate = new Date(closedDateStr.replace(' ', 'T'));
                }
                
                if (!isNaN(closedDate.getTime())) {
                  closedMap.set(actionId, closedDate);
                }
              }
            } catch (error) {
              console.error('Error parsing closed date:', closedDateStr, error);
            }
          });
        }
      });
      
      console.log('✅ Fetched closed dates for', closedMap.size, 'actions');
      return closedMap;
    } catch (error) {
      console.error('Error fetching postmortem enhancements:', error);
      return new Map();
    }
  };

  const processActionData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 Processing postmortem action status data...');
      
      // Fetch closed dates from postmortem-enhancements API
      const closedDates = await fetchClosedDates();
      setClosedDatesMap(closedDates);
      
      // Extract all actions from postmortemDetailsData
      const allActions: any[] = [];
      postmortemDetailsData.forEach((pm: any) => {
        if (pm.actionDetails && Array.isArray(pm.actionDetails)) {
          pm.actionDetails.forEach((action: any) => {
            allActions.push({
              ...action,
              incident_id: pm.incident_id,
              incidentID: pm.incidentID,
              custom_id: pm.customIncidentId,
              postmortemLink: pm.customID,
              created_on: action.createdOn || pm.createdOn,
              portfolio_name: action.portfolio || pm.causedBy_portfolio,
              platform: action.platform || pm.causedBy_platform,
              application_name: pm.causedBy_application || '',
            });
          });
        }
      });

      console.log('✅ Extracted actions:', allActions.length);

      // Process data to calculate status trends
      const processedData = processActionStatusData(allActions, closedDates);
      setStatusData(processedData);
      
    } catch (err) {
      console.error('Error processing action status:', err);
      setError('Failed to process action status data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const processActionStatusData = (actions: any[], closedDates: Map<string, Date>): ActionStatusData[] => {
    // Filter actions by date range (Jan 1, 2025 onwards)
    const filteredActions = actions.filter((action: any) => {
      if (!action.created_on) return false;
      const createdDate = new Date(action.created_on);
      return createdDate >= new Date('2025-01-01');
    });

    // Apply portfolio and platform filters if provided
    let scopedActions = filteredActions;
    if (selectedPortfolio && selectedPortfolio !== 'all') {
      scopedActions = scopedActions.filter((action: any) => 
        action.portfolio_name === selectedPortfolio
      );
    }
    if (selectedPlatform && selectedPlatform !== 'all') {
      scopedActions = scopedActions.filter((action: any) => 
        action.platform === selectedPlatform
      );
    }

    // Store all actions for dialog use
    setAllActionsData(scopedActions);

    // Get current date and calculate week ranges
    const now = new Date();
    const currentWeekNumber = getWeekNumber(now);
    const currentYear = now.getFullYear();
    
    // Generate last N completed weeks data based on selectedWeeks (e.g., 2, 3, or 4 weeks)
    // Start from last week (currentWeekNumber - 1) to exclude current incomplete week
    const weekRanges: Array<{ weekLabel: string; weekNumber: number; year: number }> = [];
    for (let i = selectedWeeks; i >= 1; i--) {
      const weekNum = currentWeekNumber - i;
      weekRanges.push({
        weekLabel: `Week ${String(weekNum).padStart(2, '0')}`,
        weekNumber: weekNum,
        year: currentYear
      });
    }
    
    setWeeks(weekRanges.map(w => w.weekLabel));

    // Group actions by Portfolio (case-insensitive)
    const groupedData: { [key: string]: any[] } = {};
    
    scopedActions.forEach((action: any) => {
      const portfolioName = action.portfolio_name || 'Portfolio not defined';
      // Normalize to title case for consistent grouping
      const key = portfolioName === 'Portfolio not defined' 
        ? portfolioName 
        : portfolioName.split(' ').map((word: string) => 
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          ).join(' ');
      
      if (!groupedData[key]) {
        groupedData[key] = [];
      }
      groupedData[key].push(action);
    });

    // Calculate status for each group
    const statusTrendsData: ActionStatusData[] = Object.keys(groupedData).map(key => {
      const actionsInGroup = groupedData[key];
      
      // Calculate weekly data
      const weeklyData: WeekData[] = weekRanges.map(weekRange => {
        const weekActions = actionsInGroup.filter((action: any) => {
          if (!action.created_on) return false;
          const actionDate = new Date(action.created_on);
          const actionWeekNum = getWeekNumber(actionDate);
          const actionYear = actionDate.getFullYear();
          return actionWeekNum === weekRange.weekNumber && actionYear === weekRange.year;
        });

        const closed = actionsInGroup.filter((action: any) => {
          // Check if this action was closed in this specific week using closedDates from API
          const actionIds: string[] = [];
          
          // Handle action.id as array or string
          if (Array.isArray(action.id)) {
            actionIds.push(...action.id);
          } else if (action.id) {
            actionIds.push(String(action.id));
          }
          
          // Check if any of the action IDs was closed in this week
          return actionIds.some(actionId => {
            const cleanActionId = actionId.trim();
            const closedDate = closedDates.get(cleanActionId);
            
            if (!closedDate) return false;
            
            // Check if closed date falls in this week
            const closedWeekNum = getWeekNumber(closedDate);
            const closedYear = closedDate.getFullYear();
            return closedWeekNum === weekRange.weekNumber && closedYear === weekRange.year;
          });
        }).length;

        // Missed = actions created in that week that are past due date now
        const today = new Date();
        const missed = weekActions.filter((action: any) => {
          const status = action.status?.toUpperCase();
          if (status === 'DONE' || status === 'COMPLETED' || status === 'CLOSED') return false;
          
          if (!action.dueDate) return false;
          
          try {
            const dateStr = action.dueDate.trim();
            let dueDate: Date | null = null;
            
            if (dateStr.includes(' ') || dateStr.split('-')[0].length <= 2) {
              const dateTimeParts = dateStr.split(' ');
              const datePart = dateTimeParts[0];
              const parts = datePart.split('-');
              if (parts.length === 3) {
                dueDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
              }
            } else {
              dueDate = new Date(dateStr);
            }
            
            if (dueDate && !isNaN(dueDate.getTime())) {
              return dueDate < today;
            }
          } catch (error) {
            return false;
          }
          return false;
        }).length;

        // Open within due = ALL currently open actions (regardless of creation date) with future due date
        // For current week, show snapshot of open actions with future due dates
        const openWithinDue = actionsInGroup.filter((action: any) => {
          const status = action.status?.toUpperCase();
          if (status === 'DONE' || status === 'COMPLETED' || status === 'CLOSED') return false;
          
          // If no due date, don't count as "within due"
          if (!action.dueDate || action.dueDate.trim() === '') return false;
          
          try {
            const dateStr = action.dueDate.trim();
            let dueDate: Date | null = null;
            
            if (dateStr.includes(' ') || dateStr.split('-')[0].length <= 2) {
              const dateTimeParts = dateStr.split(' ');
              const datePart = dateTimeParts[0];
              const parts = datePart.split('-');
              if (parts.length === 3) {
                dueDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
              }
            } else {
              dueDate = new Date(dateStr);
            }
            
            if (dueDate && !isNaN(dueDate.getTime())) {
              // For current week or latest week, show actions with future due date
              return dueDate >= today;
            }
          } catch (error) {
            return false;
          }
          return false;
        }).length;

        return {
          weekLabel: weekRange.weekLabel,
          closed,
          missed,
          openWithinDue
        };
      });

      // Calculate total open actions by due date categories
      const currentOpenActions = actionsInGroup.filter((action: any) => {
        const status = action.status?.toUpperCase();
        return status !== 'DONE' && status !== 'COMPLETED' && status !== 'CLOSED';
      });

      const today = new Date();
      const fifteenDaysFromNow = new Date();
      fifteenDaysFromNow.setDate(today.getDate() + 15);

      let openDueWithin15Days = 0;
      let openDueAfter15Days = 0;
      let openPastDueDate = 0;

      currentOpenActions.forEach((action: any) => {
        if (!action.dueDate || action.dueDate.trim() === '') {
          openDueAfter15Days++; // Actions without due date go to after 15 days
          return;
        }

        try {
          const dateStr = action.dueDate.trim();
          let dueDate: Date | null = null;
          
          if (dateStr.includes(' ') || dateStr.split('-')[0].length <= 2) {
            const dateTimeParts = dateStr.split(' ');
            const datePart = dateTimeParts[0];
            const parts = datePart.split('-');
            if (parts.length === 3) {
              dueDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
            }
          } else {
            dueDate = new Date(dateStr);
          }
          
          if (dueDate && !isNaN(dueDate.getTime())) {
            if (dueDate < today) {
              openPastDueDate++;
            } else if (dueDate <= fifteenDaysFromNow) {
              openDueWithin15Days++;
            } else {
              openDueAfter15Days++;
            }
          } else {
            openDueAfter15Days++;
          }
        } catch (error) {
          openDueAfter15Days++;
        }
      });

      return {
        portfolio: key,
        weeks: weeklyData,
        openDueWithin15Days,
        openDueAfter15Days,
        openPastDueDate
      };
    });

    // Sort by portfolio name alphabetically
    statusTrendsData.sort((a, b) => {
      return a.portfolio.localeCompare(b.portfolio);
    });

    // Calculate Grand Total row
    const grandTotal: ActionStatusData = {
      portfolio: 'Grand Total',
      weeks: weekRanges.map((weekRange, index) => ({
        weekLabel: weekRange.weekLabel,
        closed: statusTrendsData.reduce((sum, data) => sum + (data.weeks[index]?.closed || 0), 0),
        missed: statusTrendsData.reduce((sum, data) => sum + (data.weeks[index]?.missed || 0), 0),
        openWithinDue: statusTrendsData.reduce((sum, data) => sum + (data.weeks[index]?.openWithinDue || 0), 0)
      })),
      openDueWithin15Days: statusTrendsData.reduce((sum, data) => sum + data.openDueWithin15Days, 0),
      openDueAfter15Days: statusTrendsData.reduce((sum, data) => sum + data.openDueAfter15Days, 0),
      openPastDueDate: statusTrendsData.reduce((sum, data) => sum + data.openPastDueDate, 0)
    };

    return [...statusTrendsData, grandTotal];
  };

  // Helper function to get ISO week number
  const getWeekNumber = (date: Date): number => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };

  // Handle click on counts to show detail dialog
  const handleCountClick = (portfolio: string, weekIndex: number, type: 'closed' | 'missed' | 'openWithinDue' | 'dueWithin15' | 'dueAfter15' | 'pastDue') => {
    if (portfolio === 'Grand Total') return;
    
    const now = new Date();
    const currentWeekNumber = getWeekNumber(now);
    const currentYear = now.getFullYear();
    const weekNum = currentWeekNumber - (selectedWeeks - weekIndex); // Calculate actual week number based on selected weeks
    
    let filteredData: any[] = [];
    let title = '';
    
    // Filter actions for the specific portfolio (case-insensitive comparison)
    const portfolioActions = allActionsData.filter(action => {
      if (portfolio === 'Portfolio not defined') {
        return !action.portfolio_name || action.portfolio_name.trim() === '';
      }
      // Normalize both sides for comparison
      const actionPortfolio = action.portfolio_name || '';
      const normalizedActionPortfolio = actionPortfolio.split(' ').map((word: string) => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
      return normalizedActionPortfolio === portfolio;
    });
    
    if (type === 'closed' || type === 'missed' || type === 'openWithinDue') {
      if (type === 'closed') {
        // For closed actions, filter by actions that were closed in this specific week using closedDates
        filteredData = portfolioActions.filter((action: any) => {
          const actionIds: string[] = [];
          
          // Handle action.id as array or string
          if (Array.isArray(action.id)) {
            actionIds.push(...action.id);
          } else if (action.id) {
            actionIds.push(String(action.id));
          }
          
          // Check if any of the action IDs was closed in this week
          return actionIds.some(actionId => {
            const cleanActionId = actionId.trim();
            const closedDate = closedDatesMap.get(cleanActionId);
            
            if (!closedDate) return false;
            
            // Check if closed date falls in this week
            const closedWeekNum = getWeekNumber(closedDate);
            const closedYear = closedDate.getFullYear();
            return closedWeekNum === weekNum && closedYear === currentYear;
          });
        });
        
      } else if (type === 'missed') {
        // For missed actions, filter by actions created in that week that are past due date now
        filteredData = portfolioActions.filter((action: any) => {
          if (!action.created_on) return false;
          const actionDate = new Date(action.created_on);
          const actionWeekNum = getWeekNumber(actionDate);
          const actionYear = actionDate.getFullYear();
          
          if (actionWeekNum !== weekNum || actionYear !== currentYear) return false;
          
          const status = action.status?.toUpperCase();
          const isOpen = status !== 'DONE' && status !== 'COMPLETED' && status !== 'CLOSED';
          
          if (!isOpen) return false;
          if (!action.dueDate) return false;
          
          try {
            const today = new Date();
            const dateStr = action.dueDate.trim();
            let dueDate: Date | null = null;
            
            if (dateStr.includes(' ') || dateStr.split('-')[0].length <= 2) {
              const dateTimeParts = dateStr.split(' ');
              const datePart = dateTimeParts[0];
              const parts = datePart.split('-');
              if (parts.length === 3) {
                dueDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
              }
            } else {
              dueDate = new Date(dateStr);
            }
            
            if (dueDate && !isNaN(dueDate.getTime())) {
              return dueDate < today;
            }
          } catch (error) {
            return false;
          }
          return false;
        });
        
      } else { // openWithinDue - show ALL currently open actions with future due dates (snapshot)
        const today = new Date();
        filteredData = portfolioActions.filter((action: any) => {
          const status = action.status?.toUpperCase();
          const isOpen = status !== 'DONE' && status !== 'COMPLETED' && status !== 'CLOSED';
          
          if (!isOpen) return false;
          if (!action.dueDate || action.dueDate.trim() === '') return false;
          
          try {
            const dateStr = action.dueDate.trim();
            let dueDate: Date | null = null;
            
            if (dateStr.includes(' ') || dateStr.split('-')[0].length <= 2) {
              const dateTimeParts = dateStr.split(' ');
              const datePart = dateTimeParts[0];
              const parts = datePart.split('-');
              if (parts.length === 3) {
                dueDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
              }
            } else {
              dueDate = new Date(dateStr);
            }
            
            if (dueDate && !isNaN(dueDate.getTime())) {
              return dueDate >= today;
            }
          } catch (error) {
            return false;
          }
          return false;
        });
      }
      
      const weekLabel = `Week ${String(weekNum).padStart(2, '0')}`;
      const typeLabel = type === 'closed' ? 'Closed' : type === 'missed' ? 'Missed' : 'Open Within Due';
      title = `${portfolio} - ${weekLabel} - ${typeLabel} Actions`;
    } else {
      // Filter by due date categories
      const today = new Date();
      const fifteenDaysFromNow = new Date();
      fifteenDaysFromNow.setDate(today.getDate() + 15);
      
      filteredData = portfolioActions.filter((action: any) => {
        const status = action.status?.toUpperCase();
        if (status === 'DONE' || status === 'COMPLETED' || status === 'CLOSED') {
          return false;
        }
        
        if (!action.dueDate || action.dueDate.trim() === '') {
          return type === 'dueAfter15'; // Actions without due date
        }
        
        try {
          const dateStr = action.dueDate.trim();
          let dueDate: Date | null = null;
          
          if (dateStr.includes(' ') || dateStr.split('-')[0].length <= 2) {
            const dateTimeParts = dateStr.split(' ');
            const datePart = dateTimeParts[0];
            const parts = datePart.split('-');
            if (parts.length === 3) {
              dueDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
            }
          } else {
            dueDate = new Date(dateStr);
          }
          
          if (dueDate && !isNaN(dueDate.getTime())) {
            if (type === 'pastDue') {
              return dueDate < today;
            } else if (type === 'dueWithin15') {
              return dueDate >= today && dueDate <= fifteenDaysFromNow;
            } else { // dueAfter15
              return dueDate > fifteenDaysFromNow;
            }
          } else {
            return type === 'dueAfter15';
          }
        } catch (error) {
          return type === 'dueAfter15';
        }
      });
      
      const typeLabel = type === 'pastDue' ? 'Past Due Date' : type === 'dueWithin15' ? 'Due Within 15 Days' : 'Due After 15 Days';
      title = `${portfolio} - ${typeLabel} Actions`;
    }
    
    setDialogData(filteredData);
    setDialogTitle(title);
    setDialogType(type);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogData([]);
    setDialogTitle('');
    setDialogType('');
  };

  const handleExportToCSV = () => {
    try {
      const headers = [
        'Portfolio/Platform',
        ...weeks.map(w => `${w} - Closed`),
        ...weeks.map(w => `${w} - Missed`),
        ...weeks.map(w => `${w} - Open Within Due`),
        'Open Due Within 15 Days',
        'Open Due After 15 Days',
        'Open Action Past Due Date'
      ];
      
      const rows = statusData.map(row => [
        row.portfolio,
        ...row.weeks.map(w => w.closed),
        ...row.weeks.map(w => w.missed),
        ...row.weeks.map(w => w.openWithinDue),
        row.openDueWithin15Days,
        row.openDueAfter15Days,
        row.openPastDueDate
      ]);
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `postmortem_action_status_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('✅ CSV exported successfully');
    } catch (error) {
      console.error('❌ Error exporting CSV:', error);
    }
  };

  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>Loading Action Status...</Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Alert severity="error">
          <Typography variant="h6">Error Loading Action Status</Typography>
          <Typography>{error}</Typography>
          <Button onClick={processActionData} sx={{ mt: 1 }}>Retry</Button>
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Postmortem Action Status - Weekly Trends
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Chip
              label="Last 2 Weeks"
              onClick={() => setSelectedWeeks(2)}
              color={selectedWeeks === 2 ? 'primary' : 'default'}
              variant={selectedWeeks === 2 ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label="Last 3 Weeks"
              onClick={() => setSelectedWeeks(3)}
              color={selectedWeeks === 3 ? 'primary' : 'default'}
              variant={selectedWeeks === 3 ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label="Last 4 Weeks"
              onClick={() => setSelectedWeeks(4)}
              color={selectedWeeks === 4 ? 'primary' : 'default'}
              variant={selectedWeeks === 4 ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<FileDownload />}
          onClick={handleExportToCSV}
          sx={{
            textTransform: 'none',
            bgcolor: '#1976d2',
            '&:hover': { bgcolor: '#1565c0' }
          }}
        >
          Export to CSV
        </Button>
      </Box>

      <TableContainer>
        <Table size="small" sx={{ minWidth: 1200, '& .MuiTableCell-root': { border: '1px solid #000' } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#1e3a5f', borderBottom: '2px solid #000' }}>
              <TableCell 
                rowSpan={3} 
                sx={{ 
                  fontWeight: 'bold',
                  color: 'white',
                  borderRight: '2px solid #000',
                  border: '2px solid #000',
                  verticalAlign: 'middle',
                  minWidth: 200
                }}
              >
                Portfolio/Platform
              </TableCell>
              <TableCell 
                colSpan={weeks.length * 2} 
                align="center" 
                sx={{ 
                  fontWeight: 'bold',
                  color: 'white',
                  borderRight: '2px solid #000',
                  border: '2px solid #000'
                }}
              >
                Closed
              </TableCell>
              <TableCell 
                colSpan={weeks.length * 2} 
                align="center" 
                sx={{ 
                  fontWeight: 'bold',
                  color: 'white',
                  borderRight: '2px solid #000',
                  border: '2px solid #000'
                }}
              >
                Missed
              </TableCell>
              <TableCell 
                colSpan={weeks.length * 2} 
                align="center" 
                sx={{ 
                  fontWeight: 'bold',
                  color: 'white',
                  borderRight: '2px solid #000',
                  border: '2px solid #000'
                }}
              >
                Open Actions within due date
              </TableCell>
              <TableCell 
                colSpan={3} 
                align="center" 
                sx={{ 
                  fontWeight: 'bold',
                  color: 'white',
                  border: '2px solid #000'
                }}
              >
                Total Number of actions open
              </TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#1e3a5f', borderBottom: '2px solid #000' }}>
              {weeks.map((week) => (
                <TableCell 
                  key={`closed-${week}`}
                  colSpan={2}
                  align="center" 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: '0.85rem',
                    color: 'white',
                    border: '2px solid #000',
                    borderRight: '2px solid #000'
                  }}
                >
                  {week}
                </TableCell>
              ))}
              {weeks.map((week) => (
                <TableCell 
                  key={`missed-${week}`}
                  colSpan={2}
                  align="center" 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: '0.85rem',
                    color: 'white',
                    border: '2px solid #000',
                    borderRight: '2px solid #000'
                  }}
                >
                  {week}
                </TableCell>
              ))}
              {weeks.map((week) => (
                <TableCell 
                  key={`open-${week}`}
                  colSpan={2}
                  align="center" 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: '0.85rem',
                    color: 'white',
                    border: '2px solid #000',
                    borderRight: '2px solid #000'
                  }}
                >
                  {week}
                </TableCell>
              ))}
              <TableCell 
                rowSpan={2}
                align="center" 
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  color: 'white',
                  border: '2px solid #000',
                  verticalAlign: 'middle'
                }}
              >
                Open actions due within 15 days
              </TableCell>
              <TableCell 
                rowSpan={2}
                align="center" 
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  color: 'white',
                  border: '2px solid #000',
                  verticalAlign: 'middle'
                }}
              >
                Open actions due after 15 days
              </TableCell>
              <TableCell 
                rowSpan={2}
                align="center" 
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  color: 'white',
                  border: '2px solid #000',
                  verticalAlign: 'middle'
                }}
              >
                Open Action Past due date
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {statusData.map((row, index) => (
              <TableRow 
                key={row.portfolio}
                sx={{ 
                  '&:hover': { bgcolor: '#f5f5f5' },
                  bgcolor: row.portfolio === 'Grand Total' ? '#f5f5f5' : 'inherit',
                  borderTop: row.portfolio === 'Grand Total' ? '2px solid #003d82' : 'none'
                }}
              >
                <TableCell 
                  sx={{ 
                    fontWeight: row.portfolio === 'Grand Total' ? 'bold' : 'normal',
                    borderRight: '2px solid #e0e0e0'
                  }}
                >
                  {row.portfolio}
                </TableCell>
                {row.weeks.map((weekData, weekIndex) => (
                  <TableCell 
                    key={`closed-${weekIndex}`}
                    colSpan={2}
                    align="center"
                    sx={{ 
                      cursor: row.portfolio !== 'Grand Total' ? 'pointer' : 'default',
                      '&:hover': row.portfolio !== 'Grand Total' ? { bgcolor: '#f5f5f5' } : {},
                      borderRight: weekIndex === row.weeks.length - 1 ? '2px solid #e0e0e0' : '1px solid #e8e8e8'
                    }}
                    onClick={() => row.portfolio !== 'Grand Total' && handleCountClick(row.portfolio, weekIndex, 'closed')}
                  >
                    {weekData.closed}
                  </TableCell>
                ))}
                {row.weeks.map((weekData, weekIndex) => (
                  <TableCell 
                    key={`missed-${weekIndex}`}
                    colSpan={2}
                    align="center"
                    sx={{ 
                      cursor: row.portfolio !== 'Grand Total' ? 'pointer' : 'default',
                      '&:hover': row.portfolio !== 'Grand Total' ? { bgcolor: '#f5f5f5' } : {},
                      borderRight: weekIndex === row.weeks.length - 1 ? '2px solid #e0e0e0' : '1px solid #e8e8e8'
                    }}
                    onClick={() => row.portfolio !== 'Grand Total' && handleCountClick(row.portfolio, weekIndex, 'missed')}
                  >
                    {weekData.missed}
                  </TableCell>
                ))}
                {row.weeks.map((weekData, weekIndex) => (
                  <TableCell 
                    key={`open-${weekIndex}`}
                    colSpan={2}
                    align="center"
                    sx={{ 
                      cursor: row.portfolio !== 'Grand Total' ? 'pointer' : 'default',
                      '&:hover': row.portfolio !== 'Grand Total' ? { bgcolor: '#f5f5f5' } : {},
                      borderRight: weekIndex === row.weeks.length - 1 ? '2px solid #e0e0e0' : '1px solid #e8e8e8'
                    }}
                    onClick={() => row.portfolio !== 'Grand Total' && handleCountClick(row.portfolio, weekIndex, 'openWithinDue')}
                  >
                    {weekData.openWithinDue}
                  </TableCell>
                ))}
                <TableCell 
                  align="center"
                  sx={{ 
                    cursor: row.portfolio !== 'Grand Total' ? 'pointer' : 'default',
                    '&:hover': row.portfolio !== 'Grand Total' ? { bgcolor: '#ffe0b2' } : {},
                    fontWeight: 'bold',
                    bgcolor: (row.portfolio === 'Grand Total' || row.openDueWithin15Days === 0) ? 'inherit' : '#fff3e0'
                  }}
                  onClick={() => row.portfolio !== 'Grand Total' && handleCountClick(row.portfolio, 0, 'dueWithin15')}
                >
                  {row.openDueWithin15Days}
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    cursor: row.portfolio !== 'Grand Total' ? 'pointer' : 'default',
                    '&:hover': row.portfolio !== 'Grand Total' ? { bgcolor: '#c8e6c9' } : {},
                    fontWeight: 'bold',
                    bgcolor: (row.portfolio === 'Grand Total' || row.openDueAfter15Days === 0) ? 'inherit' : '#d4edda'
                  }}
                  onClick={() => row.portfolio !== 'Grand Total' && handleCountClick(row.portfolio, 0, 'dueAfter15')}
                >
                  {row.openDueAfter15Days}
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    cursor: row.portfolio !== 'Grand Total' ? 'pointer' : 'default',
                    '&:hover': row.portfolio !== 'Grand Total' ? { bgcolor: '#ffcdd2' } : {},
                    fontWeight: 'bold',
                    bgcolor: (row.portfolio === 'Grand Total' || row.openPastDueDate === 0) ? 'inherit' : '#ffebee'
                  }}
                  onClick={() => row.portfolio !== 'Grand Total' && handleCountClick(row.portfolio, 0, 'pastDue')}
                >
                  {row.openPastDueDate}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Legend */}
      <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
          Total Number of Post-Mortem Action Open:
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, bgcolor: '#fff3e0', border: '1px solid #ffb74d', borderRadius: 0.5 }} />
            <Typography variant="body2">
              <strong>Open actions due within 15 days:</strong> Actions with due date within 15 days from today
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, bgcolor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: 0.5 }} />
            <Typography variant="body2">
              <strong>Open actions due after 15 days:</strong> Actions with due date more than 15 days from today
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, bgcolor: '#ffebee', border: '1px solid #ef9a9a', borderRadius: 0.5 }} />
            <Typography variant="body2">
              <strong>Open actions past due date:</strong> Actions that have already passed their due date
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'white'
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white' }}>
          {dialogTitle}
        </DialogTitle>
        <DialogContent sx={{ p: 3, bgcolor: 'white' }}>
          {dialogData.length === 0 ? (
            <Typography>No actions found.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Incident ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Action ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Assigned To</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Portfolio</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Caused By Application</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Due Date</TableCell>
                    {dialogType === 'closed' && <TableCell sx={{ fontWeight: 'bold' }}>Closed Date</TableCell>}
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dialogData.map((action: any, index: number) => {
                    // Parse incident ID to get incident number
                    const getIncidentNumber = (incidentField: string) => {
                      if (!incidentField) return 'N/A';
                      
                      if (incidentField.includes('service-now.com')) {
                        const match = incidentField.match(/number=([A-Z0-9]+)/);
                        return match ? match[1] : 'N/A';
                      }
                      
                      if (incidentField.match(/^[A-Z]{3}\d+$/)) {
                        return incidentField;
                      }
                      
                      return incidentField;
                    };
                    
                    const incidentNumber = getIncidentNumber(action.incidentID || action.incident_id);
                    const postmortemUrl = action.postmortemLink || null;
                    
                    return (
                      <TableRow key={index} sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                        <TableCell>
                          {incidentNumber && postmortemUrl ? (
                            <a 
                              href={postmortemUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#1976d2', textDecoration: 'none' }}
                            >
                              {incidentNumber}
                            </a>
                          ) : incidentNumber}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            // Handle both array and string action IDs
                            const actionIds = Array.isArray(action.id) ? action.id : [action.id].filter(Boolean);
                            
                            if (actionIds.length === 0) return 'N/A';
                            
                            return actionIds.map((id: string, idx: number) => {
                              const trimmedId = id.trim();
                              const isPTask = trimmedId.startsWith('PTASK');
                              
                              let linkUrl = '';
                              if (isPTask) {
                                linkUrl = `https://maersk.service-now.com/nav_to.do?uri=task.do?sysparm_query=number=${trimmedId}`;
                              } else {
                                linkUrl = `https://maersk-tools.atlassian.net/browse/${trimmedId}`;
                              }
                              
                              return (
                                <React.Fragment key={idx}>
                                  {idx > 0 && ', '}
                                  <a
                                    href={linkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#1976d2', textDecoration: 'none' }}
                                  >
                                    {trimmedId}
                                  </a>
                                </React.Fragment>
                              );
                            });
                          })()}
                        </TableCell>
                        <TableCell>{action.assignee || 'N/A'}</TableCell>
                        <TableCell>{action.portfolio_name || action.portfolio || 'N/A'}</TableCell>
                        <TableCell>{action.application_name || ''}</TableCell>
                        <TableCell sx={{ maxWidth: 400 }}>
                          {action.description || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {action.dueDate ? (() => {
                            try {
                              const dateStr = action.dueDate.trim();
                              let dueDate: Date | null = null;
                              
                              // Parse date based on format
                              if (dateStr.includes(' ') || dateStr.split('-')[0].length <= 2) {
                                // PTASK format: DD-MM-YYYY HH:mm:ss
                                const dateTimeParts = dateStr.split(' ');
                                const datePart = dateTimeParts[0];
                                const parts = datePart.split('-');
                                if (parts.length === 3) {
                                  const day = parts[0];
                                  const month = parts[1];
                                  const year = parts[2];
                                  dueDate = new Date(`${year}-${month}-${day}`);
                                }
                              } else {
                                // JIRA format: YYYY-MM-DD
                                dueDate = new Date(dateStr);
                              }
                              
                              if (dueDate && !isNaN(dueDate.getTime())) {
                                return dueDate.toLocaleDateString();
                              }
                              return 'N/A';
                            } catch (error) {
                              return 'N/A';
                            }
                          })() : 'N/A'}
                        </TableCell>
                        {dialogType === 'closed' && (
                          <TableCell>
                            {(() => {
                              // Get closed date for this action from closedDatesMap
                              const actionIds = Array.isArray(action.id) ? action.id : [action.id].filter(Boolean);
                              
                              for (const actionId of actionIds) {
                                const cleanActionId = actionId.trim();
                                const closedDate = closedDatesMap.get(cleanActionId);
                                
                                if (closedDate) {
                                  return closedDate.toLocaleDateString();
                                }
                              }
                              
                              return 'N/A';
                            })()}
                          </TableCell>
                        )}
                        <TableCell>
                          <Chip 
                            label={action.status || 'N/A'} 
                            size="small"
                            sx={{
                              backgroundColor: (() => {
                                const status = action.status?.toUpperCase().replace(/\s+/g, '');
                                if (status === 'DONE' || status === 'COMPLETED' || status === 'CLOSED') return '#d4edda !important';
                                if (status === 'INPROGRESS' || status === 'PROGRESS') return '#ffe0b2 !important';
                                if (status === 'NEW' || status === 'OPEN' || status === 'TODO') return '#ffcdd2 !important';
                                if (status === 'BACKLOG') return '#e1bee7 !important';
                                if (status === 'ASSESS') return '#ffccbc !important';
                                return '#f5f5f5 !important';
                              })(),
                              color: (() => {
                                const status = action.status?.toUpperCase().replace(/\s+/g, '');
                                if (status === 'DONE' || status === 'COMPLETED' || status === 'CLOSED') return '#155724 !important';
                                if (status === 'INPROGRESS' || status === 'PROGRESS') return '#e65100 !important';
                                if (status === 'NEW' || status === 'OPEN' || status === 'TODO') return '#c62828 !important';
                                if (status === 'BACKLOG') return '#6a1b9a !important';
                                if (status === 'ASSESS') return '#d84315 !important';
                                return '#666 !important';
                              })(),
                              fontWeight: 600,
                              '& .MuiChip-label': {
                                color: (() => {
                                  const status = action.status?.toUpperCase().replace(/\s+/g, '');
                                  if (status === 'DONE' || status === 'COMPLETED' || status === 'CLOSED') return '#155724';
                                  if (status === 'INPROGRESS' || status === 'PROGRESS') return '#e65100';
                                  if (status === 'NEW' || status === 'OPEN' || status === 'TODO') return '#c62828';
                                  if (status === 'BACKLOG') return '#6a1b9a';
                                  if (status === 'ASSESS') return '#d84315';
                                  return '#666';
                                })()
                              }
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: 'white' }}>
          <Button onClick={handleCloseDialog} variant="contained" sx={{ bgcolor: '#1976d2' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PostmortemActionStatus;
